import type { ProblemDetail } from '../types';

/** Default when no status-specific copy applies and the caller gives none. */
const GENERIC_FALLBACK = 'Something went wrong. Please try again.';

/**
 * Friendly, user-safe copy for a status — or `null` when the status has no
 * context-independent message (the caller's `fallback` then applies).
 *
 * Deliberately narrow: only statuses whose meaning is the SAME everywhere get
 * fixed copy. The rest (400/401/403/404/409/422 …) are context-dependent — a
 * 401 means "bad credentials" on a login form but "session expired" on an
 * authed action — so the call site supplies the right message via `fallback`.
 * This is the single source of truth for both {@link ApiError}'s user-facing
 * `.message` and {@link toUserMessage}, so the two can never drift.
 */
function friendlyMessageForStatus(status: number): string | null {
  if (status >= 500) return 'The server had a problem. Please try again shortly.';
  if (status === 429) return 'Too many attempts. Please wait a moment and try again.';
  return null;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly title: string;
  public readonly detail: ProblemDetail['detail'];
  public readonly problem: ProblemDetail | null;
  /**
   * The raw server-provided text (problem `detail`, else `title`).
   *
   * Diagnostic only — for logging and the error reporter. NEVER render this
   * or `detail`/`title` to a user: they can carry backend internals. Anything
   * a user sees must come from `.message` or {@link toUserMessage}.
   */
  public readonly rawMessage: string;

  constructor(
    status: number,
    title: string,
    detail: ProblemDetail['detail'] = null,
    problem: ProblemDetail | null = null,
  ) {
    // `.message` is user-safe by construction: even the lazy paths
    // (`err.message`, `String(err)`, a thrown-and-logged error, a React
    // error boundary) surface friendly copy, never a backend string. Raw
    // text is quarantined on `.rawMessage` for diagnostics.
    super(friendlyMessageForStatus(status) ?? GENERIC_FALLBACK);
    this.name = 'ApiError';
    this.status = status;
    this.title = title;
    this.detail = detail;
    this.problem = problem;
    this.rawMessage = detail && typeof detail === 'string' ? detail : title;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/**
 * Coerce a `ProblemDetail.detail` value into a plain string. Pydantic's 422
 * responses come through as a list of `{loc, msg}` entries; we flatten them
 * with a separator.
 *
 * Diagnostic only — this returns raw backend text. Do NOT use it to build a
 * user-facing message; use {@link toUserMessage} instead. App code is
 * lint-blocked from importing this (see `eslint.config.js`).
 */
export function detailToString(detail: ApiError['detail']): string | null {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((entry) => (typeof entry.msg === 'string' ? entry.msg : null))
      .filter((s): s is string => Boolean(s))
      .join('; ');
  }
  return null;
}

/**
 * Map any thrown value into a friendly, user-facing sentence.
 *
 * This is the one sanctioned way to turn an error into display text. Raw API
 * strings ("bad csrf token", Pydantic validation dumps, stack messages) never
 * reach the UI — callers pass whatever they caught and get back copy that is
 * safe to show. `fallback` supplies the action-specific default ("Could not
 * save. Please try again.") used for unmapped 4xx and non-API errors.
 */
export function toUserMessage(err: unknown, fallback = GENERIC_FALLBACK): string {
  if (isApiError(err)) {
    return friendlyMessageForStatus(err.status) ?? fallback;
  }
  // A dropped connection surfaces as a TypeError from fetch, not an ApiError.
  if (err instanceof TypeError) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  return fallback;
}
